import { useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IconChevron } from '../icons/WizardIcons';

/** Ensures only one WizardSelect menu is open at a time across the page. */
const WIZARD_SELECT_OPEN_EVENT = 'wizard-select-open';

export type WizardSelectOption = {
  id: string;
  label: string;
};

type Props = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  placeholder: string;
  options: WizardSelectOption[];
  disabled?: boolean;
  loading?: boolean;
  hasError?: boolean;
  clearable?: boolean;
  emptyMessage?: string;
  /** When true, the field accepts typed input and filters options by label. */
  searchable?: boolean;
  /** Remote typeahead — skips local filtering and notifies parent on query changes. */
  remoteSearch?: {
    minLength: number;
    onQueryChange: (query: string) => void;
    hintMessage: string;
    loadingMessage?: string;
  };
};

export function fieldInputClass(hasError: boolean): string {
  return `ccr-inp${hasError ? ' inv' : ''}`;
}

function filterOptionsByQuery(options: WizardSelectOption[], query: string): WizardSelectOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.label.toLowerCase().includes(q));
}

/** Single-select dropdown aligned with MultiSelectChips (disabled look, clear, no placeholder in list). */
export default function WizardSelect({
  id,
  value,
  onChange,
  onClear,
  placeholder,
  options,
  disabled = false,
  loading = false,
  hasError = false,
  clearable = true,
  emptyMessage = 'No data found',
  searchable = false,
  remoteSearch,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const instanceId = useId();
  const listId = useId();
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const setOpenExclusive = (next: boolean | ((prev: boolean) => boolean)) => {
    setOpen((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      if (resolved) {
        window.dispatchEvent(
          new CustomEvent(WIZARD_SELECT_OPEN_EVENT, { detail: { id: instanceId } }),
        );
      }
      return resolved;
    });
  };

  const selectDisabled = disabled || (loading && !remoteSearch);
  const showClear = clearable && Boolean(value) && !disabled;
  const isSearchable = searchable || Boolean(remoteSearch);
  const selectedLabel = options.find((o) => o.id === value)?.label ?? '';
  const displayText = value ? selectedLabel : placeholder;

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !open) return options;
    if (remoteSearch) return options;
    return filterOptionsByQuery(options, query);
  }, [isSearchable, open, options, query, remoteSearch]);

  const queryBelowMin =
    Boolean(remoteSearch) && open && query.trim().length < remoteSearch!.minLength;
  const showHint = queryBelowMin;
  const showLoading = Boolean(remoteSearch) && loading && !showHint;
  const showNoData = !disabled && !showHint && !showLoading && filteredOptions.length === 0;

  useEffect(() => {
    if (!isSearchable) return;
    if (open) return;
    setQuery(selectedLabel);
  }, [isSearchable, open, selectedLabel]);

  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const anchor = isSearchable ? inputRef.current : btnRef.current;
    if (!anchor) return;
    const updatePos = () => {
      const el = isSearchable ? inputRef.current : btnRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPos({ top: rect.bottom, left: rect.left, width: rect.width });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open, isSearchable]);

  useEffect(() => {
    const onOtherOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ id: string }>).detail;
      if (detail?.id !== instanceId) setOpen(false);
    };
    window.addEventListener(WIZARD_SELECT_OPEN_EVENT, onOtherOpen);
    return () => window.removeEventListener(WIZARD_SELECT_OPEN_EVENT, onOtherOpen);
  }, [instanceId]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      if (
        !wrapRef.current?.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectDisabled) return;
    if (onClear) onClear();
    else onChange('');
    setQuery('');
    setOpenExclusive(false);
  };

  const openSearchable = () => {
    if (selectDisabled) return;
    setOpenExclusive(true);
    setQuery(selectedLabel);
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const menu =
    open && !disabled && menuPos
      ? ReactDOM.createPortal(
          <ul
            ref={menuRef}
            id={listId}
            className="wizard-sel-menu ms-chips-menu"
            role="listbox"
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxWidth: menuPos.width,
              zIndex: 1050,
              overflowX: 'hidden',
            }}
          >
            {showHint ? (
              <li>
                <span className="ms-chips-empty">{remoteSearch!.hintMessage}</span>
              </li>
            ) : showLoading ? (
              <li>
                <span className="ms-chips-empty">{remoteSearch!.loadingMessage ?? 'Loading…'}</span>
              </li>
            ) : showNoData ? (
              <li>
                <span className="ms-chips-empty">{emptyMessage}</span>
              </li>
            ) : (
              filteredOptions.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={o.id === value}
                    title={o.label}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange(o.id);
                      setQuery(o.label);
                      setOpenExclusive(false);
                    }}
                  >
                    {o.label}
                  </button>
                </li>
              ))
            )}
          </ul>,
          document.body,
        )
      : null;

  return (
    <>
      <div
        ref={wrapRef}
        className={`wizard-sel-wrap${disabled ? ' is-disabled' : ''}${loading ? ' is-loading' : ''}${showClear ? ' has-clear' : ''}${open ? ' is-open' : ''}${isSearchable ? ' is-searchable' : ''}`}
      >
        {isSearchable ? (
          <input
            ref={inputRef}
            type="text"
            id={id}
            className={`wizard-sel wizard-sel-inp ${fieldInputClass(hasError)}${value || query ? '' : ' is-empty'}`}
            style={{ color: value || query ? undefined : 'var(--t3)' }}
            disabled={selectDisabled}
            placeholder={placeholder}
            value={open ? query : displayText === placeholder ? '' : displayText}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            onFocus={() => {
              if (selectDisabled) return;
              setOpenExclusive(true);
              setQuery(selectedLabel);
              window.setTimeout(() => inputRef.current?.select(), 0);
            }}
            onClick={() => {
              if (selectDisabled) return;
              setOpenExclusive(true);
            }}
            onChange={(e) => {
              const next = e.target.value;
              setQuery(next);
              setOpenExclusive(true);
              remoteSearch?.onQueryChange(next);
              if (!next.trim() && value) {
                if (onClear) onClear();
                else onChange('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpenExclusive(false);
            }}
          />
        ) : (
          <button
            ref={btnRef}
            type="button"
            id={id}
            className={`wizard-sel ${fieldInputClass(hasError)}${value ? '' : ' is-empty'}`}
            style={{ color: value ? undefined : 'var(--t3)' }}
            disabled={selectDisabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => {
              if (selectDisabled) return;
              setOpenExclusive((v) => !v);
            }}
          >
            <span className="wizard-sel-value" title={displayText || undefined}>
              {displayText}
            </span>
          </button>
        )}
        {showClear ? (
          <button
            type="button"
            className="wizard-sel-clear"
            aria-label="Clear selection"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        ) : null}
        {isSearchable ? (
          <button
            type="button"
            className="wizard-sel-chev-btn"
            tabIndex={-1}
            aria-label="Show options"
            disabled={selectDisabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (open) {
                setOpenExclusive(false);
                return;
              }
              openSearchable();
            }}
          >
            <IconChevron expanded={open} />
          </button>
        ) : (
          <span className="wizard-sel-chev" aria-hidden>
            <IconChevron expanded={open} />
          </span>
        )}
      </div>
      {menu}
    </>
  );
}
